// Lazy, per-module ECharts loading so POS tablets only download the chart types a document uses.
import type { EChartsModule } from '../core/charts.ts'

type Installer = () => Promise<{ install: (registers: unknown) => void }>

const INSTALLERS: Record<EChartsModule, Installer> = {
  BarChart: () => import('echarts/lib/chart/bar/install.js'),
  LineChart: () => import('echarts/lib/chart/line/install.js'),
  PieChart: () => import('echarts/lib/chart/pie/install.js'),
  ScatterChart: () => import('echarts/lib/chart/scatter/install.js'),
  SankeyChart: () => import('echarts/lib/chart/sankey/install.js'),
  TreemapChart: () => import('echarts/lib/chart/treemap/install.js'),
  SunburstChart: () => import('echarts/lib/chart/sunburst/install.js'),
  HeatmapChart: () => import('echarts/lib/chart/heatmap/install.js'),
  GaugeChart: () => import('echarts/lib/chart/gauge/install.js'),
  FunnelChart: () => import('echarts/lib/chart/funnel/install.js'),
  RadarChart: () => import('echarts/lib/chart/radar/install.js'),
  CandlestickChart: () => import('echarts/lib/chart/candlestick/install.js'),
  GraphChart: () => import('echarts/lib/chart/graph/install.js'),
  BoxplotChart: () => import('echarts/lib/chart/boxplot/install.js'),
  GridComponent: () => import('echarts/lib/component/grid/install.js'),
  TooltipComponent: () => import('echarts/lib/component/tooltip/install.js'),
  LegendComponent: () => import('echarts/lib/component/legend/install.js'),
  MarkLineComponent: () => import('echarts/lib/component/marker/installMarkLine.js'),
  TitleComponent: () => import('echarts/lib/component/title/install.js'),
  AriaComponent: () => import('echarts/lib/component/aria/install.js'),
  VisualMapComponent: () => import('echarts/lib/component/visualMap/install.js'),
  CalendarComponent: () => import('echarts/lib/component/calendar/install.js'),
  DataZoomComponent: () => import('echarts/lib/component/dataZoom/install.js'),
  RadarComponent: () => import('echarts/lib/component/radar/install.js')
}

const installed = new Set<string>()
let corePromise: Promise<typeof import('echarts/core')> | null = null

export async function loadEcharts (modules: EChartsModule[], renderer: 'svg' | 'canvas') {
  corePromise ??= import('echarts/core')
  const core = await corePromise
  const wanted = [...modules, renderer === 'svg' ? 'SVGRenderer' : 'CanvasRenderer'].filter(m => !installed.has(m))
  const installers = await Promise.all(wanted.map(name => {
    if (name === 'SVGRenderer') return import('echarts/lib/renderer/installSVGRenderer.js')
    if (name === 'CanvasRenderer') return import('echarts/lib/renderer/installCanvasRenderer.js')
    const loader = INSTALLERS[name as EChartsModule]
    if (!loader) throw new Error(`No installer for ${name}`)
    return loader()
  }))
  core.use(installers.map(m => m.install) as Parameters<typeof core.use>[0])
  wanted.forEach(name => installed.add(name))
  return core
}
